const CHAT_MESSAGE_LIST_SELECTOR = [
  '.doro-mobile-chat__messages',
  '.raid-control-room.desktop .raid-control-message-list',
].join(',');
const MOBILE_ENTRY_CLASS = 'doro-mobile-chat__entry';
const DESKTOP_ENTRY_CLASS = 'raid-control-message-entry';
const NEAR_BOTTOM_MIN_PX = 72;
const NEAR_BOTTOM_VIEWPORT_RATIO = 0.12;
const INITIAL_SETTLE_FRAMES = 12;
const MESSAGE_SETTLE_FRAMES = 6;

let installedCleanup = null;

function nearBottomRange(messageList) {
  return Math.max(NEAR_BOTTOM_MIN_PX, messageList.clientHeight * NEAR_BOTTOM_VIEWPORT_RATIO);
}

function isNearBottom(messageList) {
  if (!messageList) return false;
  const gap = Math.max(messageList.scrollHeight - messageList.scrollTop - messageList.clientHeight, 0);
  return gap <= nearBottomRange(messageList);
}

function messageEntries(messageList) {
  const entryClass = messageList.classList.contains('doro-mobile-chat__messages')
    ? MOBILE_ENTRY_CLASS
    : DESKTOP_ENTRY_CLASS;
  return Array.from(messageList.children).filter((child) => child.classList?.contains(entryClass));
}

function visibleEntryCount(messageList) {
  return messageEntries(messageList).filter((entry) => {
    if (!entry.querySelector('article')) return false;
    return window.getComputedStyle(entry).display !== 'none';
  }).length;
}

function createLatestScrollController(messageList) {
  const host = messageList.closest('.doro-mobile-chat, .raid-control-chat');
  if (!host) return null;

  const jumpButton = document.createElement('button');
  jumpButton.type = 'button';
  jumpButton.className = 'raid-control-latest-jump-owner';
  jumpButton.setAttribute('aria-label', '최신 메시지로 이동');
  jumpButton.hidden = true;
  host.appendChild(jumpButton);

  let followLatest = true;
  let newMessageCount = 0;
  let previousEntryCount = visibleEntryCount(messageList);
  let settleFramesRemaining = 0;
  let settleFrame = 0;
  let destroyed = false;

  const renderJumpButton = () => {
    if (destroyed) return;
    jumpButton.hidden = followLatest && messageList.dataset.history !== 'true';
    jumpButton.textContent = newMessageCount > 0
      ? `새 메시지 ${newMessageCount}개 ↓`
      : '최신 메시지 ↓';
  };

  const pinLatest = () => {
    if (!destroyed && messageList.dataset.history !== 'true') messageList.scrollTop = messageList.scrollHeight;
  };

  const runSettleFrame = () => {
    settleFrame = 0;
    if (destroyed || !followLatest) return;
    pinLatest();
    if (settleFramesRemaining <= 0) return;
    settleFramesRemaining -= 1;
    settleFrame = window.requestAnimationFrame(runSettleFrame);
  };

  const settleLatest = (frames = MESSAGE_SETTLE_FRAMES) => {
    if (destroyed) return;
    followLatest = true;
    newMessageCount = 0;
    settleFramesRemaining = Math.max(settleFramesRemaining, frames);
    pinLatest();
    renderJumpButton();
    if (!settleFrame) settleFrame = window.requestAnimationFrame(runSettleFrame);
  };

  const handleScroll = () => {
    if (destroyed) return;
    if (messageList.dataset.history === 'true') { followLatest = false; renderJumpButton(); return; }
    if (isNearBottom(messageList)) {
      followLatest = true;
      newMessageCount = 0;
    } else if (!settleFramesRemaining) {
      followLatest = false;
    }
    renderJumpButton();
  };

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => {
      if (followLatest) settleLatest(2);
      else renderJumpButton();
    })
    : null;

  const syncResizeTargets = () => {
    if (!resizeObserver) return;
    resizeObserver.disconnect();
    resizeObserver.observe(messageList);
    for (const entry of messageEntries(messageList)) resizeObserver.observe(entry);
  };

  const handleMutations = () => {
    if (destroyed) return;
    const nextEntryCount = visibleEntryCount(messageList);
    const added = Math.max(nextEntryCount - previousEntryCount, 0);
    previousEntryCount = nextEntryCount;
    syncResizeTargets();

    if (messageList.dataset.history === 'true') { followLatest = false; renderJumpButton(); return; }
    if (followLatest) {
      settleLatest(MESSAGE_SETTLE_FRAMES);
      return;
    }
    if (added > 0) newMessageCount += added;
    renderJumpButton();
  };

  const mutationObserver = new MutationObserver(handleMutations);
  mutationObserver.observe(messageList, { childList: true });
  syncResizeTargets();

  messageList.addEventListener('scroll', handleScroll, { passive: true });
  jumpButton.addEventListener('click', () => {
    messageList.dispatchEvent(new Event('raid-chat-latest'));
    window.requestAnimationFrame(() => settleLatest(MESSAGE_SETTLE_FRAMES));
  });
  settleLatest(INITIAL_SETTLE_FRAMES);

  return () => {
    destroyed = true;
    if (settleFrame) window.cancelAnimationFrame(settleFrame);
    messageList.removeEventListener('scroll', handleScroll);
    mutationObserver.disconnect();
    resizeObserver?.disconnect();
    jumpButton.remove();
  };
}

export function installRaidControlChatLatestScroll() {
  if (installedCleanup || typeof window === 'undefined' || typeof document === 'undefined') {
    return installedCleanup || (() => {});
  }

  const controllers = new Map();
  let syncFrame = 0;

  const sync = () => {
    syncFrame = 0;
    const currentLists = new Set(document.querySelectorAll(CHAT_MESSAGE_LIST_SELECTOR));

    for (const [messageList, cleanup] of controllers) {
      if (currentLists.has(messageList) && messageList.isConnected) continue;
      cleanup();
      controllers.delete(messageList);
    }

    for (const messageList of currentLists) {
      if (controllers.has(messageList)) continue;
      const cleanup = createLatestScrollController(messageList);
      if (cleanup) controllers.set(messageList, cleanup);
    }
  };

  const queueSync = () => {
    if (!syncFrame) syncFrame = window.requestAnimationFrame(sync);
  };

  const documentObserver = new MutationObserver(queueSync);
  documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  sync();

  installedCleanup = () => {
    if (syncFrame) window.cancelAnimationFrame(syncFrame);
    documentObserver.disconnect();
    for (const cleanup of controllers.values()) cleanup();
    controllers.clear();
    installedCleanup = null;
  };
  return installedCleanup;
}
